'use strict'

import CXImageAction from './ImagePaintProcessing/CXImageAction.js'
import Config from './Config.js'
import { Selector } from './ui/Selectors.js'

/**
 * ActionHistory
 * --------------------------------------------------------------------
 * Centralized undo/redo stack for CanvasState.
 * Owns the action list + redo buffer and applies them back to CanvasState.
 */
class ActionHistory {
    /**
     * @param {Object} params
     * @param {CanvasState} params.canvasState
     * @param {Object} params.actionTypes
     */
    constructor({ canvasState, actionTypes }) {
        this.canvasState = canvasState
        this.actionTypes = actionTypes
        this._actions = []
        this._redoAction = undefined
    }

    /**
     * Reset stacks for a new project.
     * @returns {void}
     */
    reset() {
        this._actions = []
        this._redoAction = undefined
    }

    /**
     * Clear only the redo action.
     * @returns {void}
     */
    clearRedo() {
        this._redoAction = undefined
    }

    /**
     * Push an action to the undo stack and update button states.
     * @param {CXImageAction} action
     * @returns {void}
     */
    add(action) {
        this._actions.push(action)

        if (this._actions.length > 1) {
            $(Selector.btnUndo).removeClass('disabledbutton')
        } else {
            $(Selector.btnRedo).addClass('disabledbutton')
        }
    }

    /**
     * Return the action stack.
     * @returns {Array<CXImageAction>}
     */
    get actions() {
        return this._actions
    }

    /**
     * Return the most recent action from the stack.
     * @returns {CXImageAction|undefined}
     */
    getLastAction() {
        return this._actions[this._actions.length - 1]
    }

    /**
     * Return the most recent magic-paint action.
     * @returns {CXImageAction|null}
     */
    getLastMagicPaintAction() {
        for (let i = this._actions.length - 1; i >= 0; i--) {
            const a = this._actions[i]
            if (a && a.actionType === CXImageAction.ActionType.ActionTypeMagicPaint) {
                return a
            }
        }
        return null
    }

    /**
     * Undo the last action and refresh the mask/canvas state.
     * @returns {void}
     */
    undo() {
        const cs = this.canvasState
        const types = this.actionTypes
        const anAction = this._actions.pop()
        this._redoAction = anAction
        cs.viewimage.loadData()

        if ($(Selector.btnRedo).hasClass('disabledbutton')) {
            $(Selector.btnRedo).removeClass('disabledbutton')
        }

        if (anAction.actionType === types.ActionTypeEraseMaskColor) {
            // Restore snapshot into existing ColorSample instance
            cs._restoreColorSampleFromSnapshot(anAction.eraseColor.colors, anAction.eraseColor.workingColorPos)

            // decalage des couleurs
            let color
            const maxIter = cs._maskImage.length
            for (let i = 0; i < maxIter; i += 1) {
                color = cs._maskImage.data[i]
                if (color < Config.DEFAULT_MASKING_COLOR && color >= anAction.erasedColorMaskNumber) {
                    cs._maskImage.data[i] = color + 1
                }
            }
        } else if (anAction.actionType === types.ActionTypeChangeColorSample) {
            // Restore previous color sample + working slot
            cs._restoreColorSampleFromSnapshot(anAction.oldColor, anAction.oldWorkingPos)
            cs.rebuildPaintedImage()
        }

        if (anAction.actionType !== types.ActionTypeChangeColorSample) {
            let pixels

            if (anAction.actionType === types.ActionTypeMagicPaint) {
                pixels = anAction.lastLevelpixels().getPixels().slice(0, anAction.pixelsPerLevelIndexes[anAction.paintLevel])
            } else {
                pixels = anAction.lastLevelpixels().getPixels()
            }

            for (let i = 0; i < pixels.length; i++) {
                if (anAction.actionType === types.ActionTypeErase || anAction.actionType === types.ActionTypeEraseMaskColor) {
                    cs._maskImage.data[pixels[i][0]] = pixels[i][1]
                    cs.refreshResultAtPosition(pixels[i])
                } else {
                    cs._maskImage.data[pixels[i][0]] = Config.DEFAULT_MASKING_COLOR
                    cs.refreshResultAtPosition(pixels[i])
                }
            }

            cs.viewimage.context.putImageData(cs.viewimage.data, 0, 0)
        }

        if (this._actions.length > 1) {
            $(Selector.btnUndo).removeClass('disabledbutton')
        } else {
            $(Selector.btnUndo).addClass('disabledbutton')
        }
    }

    /**
     * Redo the last undone action.
     * @returns {void}
     */
    redo() {
        const cs = this.canvasState
        const types = this.actionTypes
        if (typeof this._redoAction === 'undefined') return

        const anAction = this._redoAction

        if (anAction.actionType === types.ActionTypeEraseMaskColor) {
            // decalage des couleurs
            let color
            const maxIter = cs._maskImage.length
            for (let i = 0; i < maxIter; i += 1) {
                color = cs._maskImage.data[i]
                if (color < Config.DEFAULT_MASKING_COLOR && color >= anAction.erasedColorMaskNumber) {
                    cs._maskImage.data[i] = color - 1
                }
            }
        }

        if (anAction.actionType !== types.ActionTypeChangeColorSample) {
            let pixels

            if (anAction.actionType === types.ActionTypeMagicPaint) {
                pixels = anAction.lastLevelpixels().getPixels().slice(0, anAction.pixelsPerLevelIndexes[anAction.paintLevel])
            } else {
                pixels = anAction.lastLevelpixels().getPixels()
            }

            for (let i = 0; i < pixels.length; i++) {
                if (anAction.actionType === types.ActionTypeErase || anAction.actionType === types.ActionTypeEraseMaskColor) {
                    cs._maskImage.data[pixels[i][0]] = Config.DEFAULT_MASKING_COLOR
                    cs.refreshResultAtPosition(pixels[i])
                } else {
                    cs._maskImage.data[pixels[i][0]] = pixels[i][1]
                    cs.refreshResultAtPosition(pixels[i])
                }
            }

            cs.viewimage.context.putImageData(cs.viewimage.data, 0, 0)
        }

        if (anAction.actionType === types.ActionTypeEraseMaskColor) {
            // re-apply the delete on the current ColorSample
            cs.colorSample.deleteColorNumber(anAction.erasedColorMaskNumber)
            // deleteColorNumber already calls updateColorInfos(), so no need
        } else if (anAction.actionType === types.ActionTypeChangeColorSample) {
            // Apply the “new” color sample
            cs._restoreColorSampleFromSnapshot(anAction.color, anAction.newWorkingPos)
            cs.rebuildPaintedImage()
        }

        this.add(anAction)
        this._redoAction = undefined

        $(Selector.btnRedo).addClass('disabledbutton')
    }
}

export default ActionHistory
